import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, RefreshCw, Truck, LogOut, ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from '../components/ui/sonner';
import { useVehicles } from '../hooks/useVehicles';
import AdminHeader from '../components/AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehicleManagement = () => {
  const navigate = useNavigate();
  const { vehicles, loading, refetch, invalidateCache } = useVehicles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [deletingVehicleId, setDeletingVehicleId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'PKW',
    description: '',
    daily_price: '',
    weekly_price: '',
    monthly_price: '',
    image_url: '',
    additional_images: [],
    features: '',
    available: true,
    quantity: '1',
    featured: false
  });
  const [mainImage, setMainImage] = useState(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    console.log('VehicleManagement - Checking auth, token exists:', !!token);
    
    if (!token) {
      console.log('VehicleManagement - No token found, redirecting to login');
      navigate('/login', { replace: true });
      return false;
    }

    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('VehicleManagement - Token valid:', response.data);
      return true;
    } catch (error) {
      console.log('VehicleManagement - Token invalid, removing and redirecting');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_token_time');
      navigate('/login', { replace: true });
      return false;
    }
  };

  useEffect(() => {
    // Force scroll to top on mount
    window.scrollTo(0, 0);
    
    const init = async () => {
      const isAuth = await checkAuth();
      if (!isAuth) {
        // Auth check handles redirect
      }
      // Vehicle data is already being fetched by useVehicles hook
    };
    init();
  }, []); // eslint-disable-line

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'PKW',
      description: '',
      daily_price: '',
      weekly_price: '',
      monthly_price: '',
      image_url: '',
      additional_images: [],
      features: '',
      available: true,
      quantity: '1',
      featured: false
    });
    setMainImage(null);
    setAdditionalImageFiles([]);
    setEditingVehicle(null);
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type.startsWith('image/')) {
        reject(new Error('Ungültige Datei'));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Safari CORS fix
        img.src = e.target.result;
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Resize if too large - more aggressive compression
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d', { willReadFrequently: false, alpha: false });
            
            // Safari compatibility: ensure proper canvas rendering
            if (ctx) {
              // White background for JPEGs
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, width, height);
              
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(img, 0, 0, width, height);
              
              // Convert to compressed base64 with optimized quality
              try {
                const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                resolve(compressedBase64);
              } catch (canvasError) {
                console.error('Canvas toDataURL error:', canvasError);
                // Fallback: return original file as base64
                resolve(e.target.result);
              }
            } else {
              reject(new Error('Canvas Kontext konnte nicht erstellt werden'));
            }
          } catch (err) {
            console.error('Image compression error:', err);
            reject(err);
          }
        };
        img.onerror = (err) => {
          console.error('Image load error:', err);
          reject(new Error('Bild konnte nicht geladen werden'));
        };
      };
      reader.onerror = (err) => {
        console.error('FileReader error:', err);
        reject(new Error('Datei konnte nicht gelesen werden'));
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    setUploading(true);

    try {
      // Compress images in parallel for better performance
      const compressionPromises = [];
      
      // Compress main image
      if (mainImage) {
        setUploadProgress('Bilder werden komprimiert...');
        compressionPromises.push(
          compressImage(mainImage, 1000, 0.80) // Reduced from 1200/0.85
            .then(compressed => ({ type: 'main', data: compressed }))
        );
      }

      // Compress additional images in parallel with more aggressive settings
      if (additionalImageFiles.length > 0) {
        additionalImageFiles.forEach((file, index) => {
          compressionPromises.push(
            compressImage(file, 800, 0.75) // Reduced from 1000/0.85
              .then(compressed => ({ type: 'additional', index, data: compressed }))
              .catch(err => {
                console.error(`Fehler beim Komprimieren von Bild ${index + 1}:`, err);
                return null;
              })
          );
        });
      }

      // Wait for all compressions to complete
      const results = await Promise.all(compressionPromises);
      
      let mainImageUrl = formData.image_url;
      const additionalImagesUrls = [];

      // Process results
      results.forEach(result => {
        if (result) {
          if (result.type === 'main') {
            mainImageUrl = result.data;
          } else if (result.type === 'additional') {
            additionalImagesUrls.push(result.data);
          }
        }
      });
      
      setUploadProgress('Fahrzeug wird gespeichert...');

      const vehicleData = {
        ...formData,
        daily_price: formData.daily_price ? parseFloat(formData.daily_price) : null,
        weekly_price: formData.weekly_price ? parseFloat(formData.weekly_price) : null,
        monthly_price: formData.monthly_price ? parseFloat(formData.monthly_price) : null,
        quantity: formData.quantity ? parseInt(formData.quantity) : 1,
        features: formData.features ? formData.features.split(',').map(f => f.trim()) : [],
        image_url: mainImageUrl,
        additional_images: additionalImagesUrls.length > 0 ? additionalImagesUrls : formData.additional_images || []
      };

      if (editingVehicle) {
        await axios.patch(`${API}/vehicles/${editingVehicle.id}`, vehicleData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fahrzeug erfolgreich aktualisiert!");
      } else {
        await axios.post(`${API}/vehicles/`, vehicleData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fahrzeug erfolgreich hinzugefügt!");
      }

      setIsDialogOpen(false);
      resetForm();
      invalidateCache(); // Invalidate cache
      await refetch(true); // Force refresh
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(editingVehicle ? "Fehler beim Aktualisieren" : "Fehler beim Hinzufügen");
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name,
      category: vehicle.category,
      description: vehicle.description || '',
      daily_price: vehicle.daily_price || '',
      weekly_price: vehicle.weekly_price || '',
      monthly_price: vehicle.monthly_price || '',
      image_url: vehicle.image_url || '',
      additional_images: vehicle.additional_images || [],
      features: vehicle.features?.join(', ') || '',
      available: vehicle.available,
      quantity: vehicle.quantity || '1',
      featured: vehicle.featured || false
    });
    setMainImage(null);
    setAdditionalImageFiles([]);
    setIsDialogOpen(true);
  };

  const handleDelete = async (vehicleId) => {
    // Double confirmation for safety
    const firstConfirm = window.confirm('Sind Sie sicher, dass Sie dieses Fahrzeug löschen möchten?');
    if (!firstConfirm) {
      return;
    }

    setDeletingVehicleId(vehicleId);
    const token = localStorage.getItem('admin_token');
    
    try {
      console.log('Deleting vehicle:', vehicleId);
      const response = await axios.delete(`${API}/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Delete response:', response);
      
      toast.success("Fahrzeug erfolgreich gelöscht!");
      invalidateCache();
      await refetch(true);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setDeletingVehicleId(null);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'PKW':
        return 'bg-blue-500';
      case 'LKW':
        return 'bg-green-500';
      case 'Baumaschinen':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="Fahrzeug-Verwaltung" subtitle="Fahrzeugverwaltung" />

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C3E50]">Fahrzeug-Verwaltung</h1>
            <p className="text-gray-600 mt-2">Fahrzeuge und Maschinen hinzufügen, bearbeiten oder löschen</p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 w-full sm:w-auto">
            <Button 
              onClick={() => refetch(true)} 
              variant="outline" 
              disabled={loading}
              className="flex-1 sm:flex-initial"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] flex-1 sm:flex-initial">
                  <Plus className="mr-2 h-4 w-4" />
                  Neues Fahrzeug
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingVehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug hinzufügen'}</DialogTitle>
                  <DialogDescription>
                    Füllen Sie die Informationen aus
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Kategorie *</Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FDB913]"
                        required
                      >
                        <option value="PKW">PKW</option>
                        <option value="LKW">LKW</option>
                        <option value="Baumaschinen">Baumaschinen</option>
                        <option value="Sonstiges">Sonstiges</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Beschreibung</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="daily_price">Tagespreis (€)</Label>
                      <Input
                        id="daily_price"
                        name="daily_price"
                        type="number"
                        step="0.01"
                        value={formData.daily_price}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="weekly_price">Wochenpreis (€)</Label>
                      <Input
                        id="weekly_price"
                        name="weekly_price"
                        type="number"
                        step="0.01"
                        value={formData.weekly_price}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="monthly_price">Monatspreis (€)</Label>
                      <Input
                        id="monthly_price"
                        name="monthly_price"
                        type="number"
                        step="0.01"
                        value={formData.monthly_price}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image_url">Bild-URL</Label>
                    <Input
                      id="image_url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="features">Ausstattung (mit Komma trennen)</Label>
                    <Input
                      id="features"
                      name="features"
                      value={formData.features}
                      onChange={handleInputChange}
                      placeholder="GPS, Klimaanlage, 4x4"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity">Anzahl verfügbar *</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      required
                    />
                    <p className="text-xs text-gray-500">Wie viele Einheiten haben Sie auf Lager?</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Verfügbarkeit</Label>
                    <select
                      value={formData.available ? "true" : "false"}
                      onChange={(e) => setFormData(prev => ({ ...prev, available: e.target.value === "true" }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FDB913]"
                    >
                      <option value="true">Verfügbar</option>
                      <option value="false">Nicht verfügbar</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id="featured"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#FDB913] border-gray-300 rounded focus:ring-[#FDB913]"
                    />
                    <Label htmlFor="featured" className="cursor-pointer">Auf Startseite anzeigen</Label>
                  </div>

                  <div className="space-y-2 pt-2">
                    <Label htmlFor="mainImage">Hauptbild</Label>
                    <input
                      type="file"
                      id="mainImage"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setMainImage(file);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FDB913]"
                    />
                    {mainImage && (
                      <div className="mt-2">
                        <p className="text-xs text-green-600 mb-2">✓ {mainImage.name} ausgewählt</p>
                        <img 
                          src={URL.createObjectURL(mainImage)} 
                          alt="Vorschau" 
                          className="w-32 h-32 object-cover rounded border"
                        />
                      </div>
                    )}
                    {formData.image_url && !mainImage && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-2">Aktuelles Bild:</p>
                        <img 
                          src={formData.image_url} 
                          alt="Aktuell" 
                          className="w-32 h-32 object-cover rounded border"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additionalImages">Weitere Bilder (max. 5)</Label>
                    <input
                      type="file"
                      id="additionalImages"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files);
                        // Anhängen statt ersetzen - max 5 Bilder insgesamt
                        const combinedFiles = [...additionalImageFiles, ...newFiles].slice(0, 5);
                        setAdditionalImageFiles(combinedFiles);
                        // Reset input to allow same file again
                        e.target.value = '';
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FDB913]"
                    />
                    {additionalImageFiles.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-green-600 mb-2">✓ {additionalImageFiles.length} Bild(er) ausgewählt (Max. 5)</p>
                        <div className="flex gap-2 flex-wrap">
                          {additionalImageFiles.map((file, idx) => (
                            <div key={idx} className="relative">
                              <img 
                                src={URL.createObjectURL(file)} 
                                alt={`Vorschau ${idx + 1}`} 
                                className="w-20 h-20 object-cover rounded border"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setAdditionalImageFiles(prev => prev.filter((_, i) => i !== idx));
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {uploading && uploadProgress && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                      ⏳ {uploadProgress}
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="submit" 
                      disabled={uploading}
                      className="flex-1 bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? 'Wird gespeichert...' : (editingVehicle ? 'Aktualisieren' : 'Hinzufügen')}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      disabled={uploading}
                      onClick={() => {
                        setIsDialogOpen(false);
                        resetForm();
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-12 w-12 animate-spin mx-auto text-[#FDB913] mb-4" />
            <p className="text-gray-600">Lade Fahrzeuge...</p>
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Truck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Noch keine Fahrzeuge vorhanden</p>
              <p className="text-gray-500 text-sm mt-2">Klicken Sie auf "Neues Fahrzeug" um eines hinzuzufügen</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="border-2 hover:border-[#FDB913] transition-all">
                {vehicle.image_url && (
                  <div className="relative group">
                    <img 
                      src={vehicle.image_url} 
                      alt={vehicle.name}
                      loading="lazy"
                      className="w-full h-48 object-cover rounded-t-lg"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    {vehicle.additional_images && vehicle.additional_images.length > 0 && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                        +{vehicle.additional_images.length} Bilder
                      </div>
                    )}
                  </div>
                )}
                {vehicle.additional_images && vehicle.additional_images.length > 0 && (
                  <div className="px-4 pt-2">
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {vehicle.additional_images.slice(0, 4).map((img, idx) => (
                        <img 
                          key={idx}
                          src={img} 
                          alt={`${vehicle.name} ${idx + 1}`}
                          loading="lazy"
                          className="w-16 h-16 object-cover rounded border cursor-pointer hover:scale-110 transition-transform flex-shrink-0"
                        />
                      ))}
                    </div>
                  </div>
                )}
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl text-[#2C3E50]">{vehicle.name}</CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge className={`${getCategoryColor(vehicle.category)} text-white`}>
                          {vehicle.category}
                        </Badge>
                        <Badge className={vehicle.available ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                          {vehicle.available ? 'Verfügbar' : 'Nicht verfügbar'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {vehicle.description && (
                    <p className="text-gray-600 text-sm mb-4">{vehicle.description}</p>
                  )}
                  
                  {(vehicle.daily_price || vehicle.weekly_price || vehicle.monthly_price) && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="font-semibold text-[#2C3E50] mb-2">Preise:</p>
                      {vehicle.daily_price && <p className="text-sm">Tag: {vehicle.daily_price} €</p>}
                      {vehicle.weekly_price && <p className="text-sm">Woche: {vehicle.weekly_price} €</p>}
                      {vehicle.monthly_price && <p className="text-sm">Monat: {vehicle.monthly_price} €</p>}
                    </div>
                  )}

                  <div className="bg-blue-50 rounded-lg p-3 mb-4 border border-blue-200">
                    <p className="font-semibold text-[#2C3E50] mb-1">Bestand:</p>
                    <p className="text-lg font-bold text-blue-600">{vehicle.quantity || 1} Einheit(en) verfügbar</p>
                  </div>

                  {vehicle.features && vehicle.features.length > 0 && (
                    <div className="mb-4">
                      <p className="font-semibold text-[#2C3E50] mb-2 text-sm">Ausstattung:</p>
                      <div className="flex flex-wrap gap-1">
                        {vehicle.features.map((feature, idx) => (
                          <span key={idx} className="text-xs bg-[#FDB913]/20 px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleEdit(vehicle)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-[#2C3E50] hover:bg-[#FDB913] hover:text-[#1a1a1a]"
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Bearbeiten
                    </Button>
                    <Button 
                      onClick={() => handleDelete(vehicle.id)}
                      variant="outline"
                      size="sm"
                      disabled={deletingVehicleId === vehicle.id}
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white disabled:opacity-50"
                    >
                      {deletingVehicleId === vehicle.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default VehicleManagement;
